import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useFieldArray, useForm } from 'react-hook-form'
import { FormWrapper, FormInput } from 'src/shared/components/FormComponents'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import { paths } from 'src/routes/paths'

import { useCreateDecorations } from 'src/api/db/apiDecorationInfo'

import { DEFAULT_ROW, formSchema } from './consts'
import type { AddDecorationsFormValues } from './types'

export const AddDecorationsPage = () => {
  const navigate = useNavigate()
  const createDecorations = useCreateDecorations()

  const methods = useForm<AddDecorationsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { rows: [DEFAULT_ROW] },
  })

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'rows',
  })

  const handleSubmit = async (data: AddDecorationsFormValues) => {
    const inserts = data.rows
      .filter((r) => r.name.trim() || r.print_code.trim())
      .map((r) => ({
        name: r.name.trim() || null,
        print_code: r.print_code.trim() || null,
        locations: r.locations.trim() || null,
        vendor: r.vendor.trim() || null,
        production_time_days: r.production_time_days
          ? Number.isNaN(Number(r.production_time_days))
            ? null
            : Number(r.production_time_days)
          : null,
      }))
    if (inserts.length === 0) return
    try {
      await createDecorations.mutateAsync(inserts)
      navigate(paths.adminDecorationsCosts)
    } catch (error) {
      console.error('Failed to create decorations:', error)
    }
  }

  return (
    <PageContainer width="xl">
      <Column className="gap-4">
        <Row className="gap-2 items-center mb-4 py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(paths.adminDecorationsCosts)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Plus className="h-6 w-6" />
          <h1>Add decorations</h1>
        </Row>

        <FormWrapper methods={methods} onSubmit={handleSubmit}>
          <Column className="gap-4">
            <Column className="border rounded-md overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]" />
                    <TableHead>Name</TableHead>
                    <TableHead>Print code</TableHead>
                    <TableHead>Locations</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="w-24">Prod. days</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, i) => (
                    <TableRow key={field.id}>
                      <TableCell className="w-[40px] p-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => remove(i)}
                          disabled={fields.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="p-1">
                        <FormInput
                          name={`rows.${i}.name`}
                          methods={methods}
                          placeholder="Name"
                          className="h-9 border-0 bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <FormInput
                          name={`rows.${i}.print_code`}
                          methods={methods}
                          placeholder="Print code"
                          className="h-9 border-0 bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <FormInput
                          name={`rows.${i}.locations`}
                          methods={methods}
                          placeholder="Locations"
                          className="h-9 border-0 bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <FormInput
                          name={`rows.${i}.vendor`}
                          methods={methods}
                          placeholder="Vendor"
                          className="h-9 border-0 bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        <FormInput
                          name={`rows.${i}.production_time_days`}
                          methods={methods}
                          type="number"
                          placeholder="Days"
                          className="h-9 border-0 bg-transparent focus-visible:ring-1"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Column>

            <Row className="gap-2 justify-between items-center">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => append(DEFAULT_ROW)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add row
              </Button>
              <Row className="gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(paths.adminDecorationsCosts)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createDecorations.isPending}
                >
                  Add
                </Button>
              </Row>
            </Row>
          </Column>
        </FormWrapper>
      </Column>
    </PageContainer>
  )
}
